/**
 * StatusBadge Component - Muted status badge for leads
 * Reusable across cards, table, and side panel
 */

import React from "react";
import { Chip } from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledChip = styled(Chip)({
  fontWeight: 500,
  fontSize: "0.75rem",
  height: "24px",
  "&.status-new": {
    backgroundColor: "rgba(25, 118, 210, 0.1)",
    color: "rgba(25, 118, 210, 0.9)",
    borderColor: "rgba(25, 118, 210, 0.3)",
  },
  "&.status-pending": {
    backgroundColor: "rgba(237, 108, 2, 0.1)",
    color: "rgba(237, 108, 2, 0.9)",
    borderColor: "rgba(237, 108, 2, 0.3)",
  },
  "&.status-pending_identity": {
    backgroundColor: "rgba(237, 108, 2, 0.1)",
    color: "rgba(237, 108, 2, 0.9)",
    borderColor: "rgba(237, 108, 2, 0.3)",
  },
  "&.status-accepted": {
    backgroundColor: "rgba(46, 125, 50, 0.1)",
    color: "rgba(46, 125, 50, 0.9)",
    borderColor: "rgba(46, 125, 50, 0.3)",
  },
  "&.status-rejected": {
    backgroundColor: "rgba(211, 47, 47, 0.1)",
    color: "rgba(211, 47, 47, 0.9)",
    borderColor: "rgba(211, 47, 47, 0.3)",
  },
  "&.status-qualified": {
    backgroundColor: "rgba(123, 31, 162, 0.1)",
    color: "rgba(123, 31, 162, 0.95)",
    borderColor: "rgba(123, 31, 162, 0.35)",
  },
  "&.status-converted": {
    backgroundColor: "rgba(158, 158, 158, 0.1)",
    color: "rgba(158, 158, 158, 0.9)",
    borderColor: "rgba(158, 158, 158, 0.3)",
  },
  "&.status-default": {
    backgroundColor: "rgba(158, 158, 158, 0.1)",
    color: "rgba(158, 158, 158, 0.9)",
    borderColor: "rgba(158, 158, 158, 0.3)",
  },
});

export const PENDING_IDENTITY_STATUS = "pending_identity";
export const PENDING_IDENTITY_LABEL = "Waiting for email confirm";

export const getLeadStatusLabel = (status) => {
  switch (status) {
    case "new":
      return "New";
    case "pending":
      return "Pending";
    case PENDING_IDENTITY_STATUS:
      return PENDING_IDENTITY_LABEL;
    case "accepted":
      return "Accepted";
    case "qualified":
      return "On Marketplace";
    case "rejected":
      return "Rejected";
    case "converted":
      return "Converted";
    default:
      return status || "N/A";
  }
};

const getStatusClass = (status) => {
  switch (status) {
    case "new":
      return "status-new";
    case "pending":
      return "status-pending";
    case PENDING_IDENTITY_STATUS:
      return "status-pending_identity";
    case "accepted":
      return "status-accepted";
    case "qualified":
      return "status-qualified";
    case "rejected":
      return "status-rejected";
    case "converted":
      return "status-converted";
    default:
      return "status-default";
  }
};

/**
 * StatusBadge Component
 * @param {Object} props
 * @param {string} props.status - Lead status
 * @param {string} [props.size="small"] - Chip size
 * @param {string} [props.variant="outlined"] - Chip variant
 */
const StatusBadge = ({ status, size = "small", variant = "outlined" }) => {
  if (!status) return null;

  return (
    <StyledChip
      label={getLeadStatusLabel(status)}
      size={size}
      variant={variant}
      className={getStatusClass(status)}
    />
  );
};

export default StatusBadge;

